/**
 * Array-String Defensive Utilities
 *
 * Provides safe handling for fields that might be strings or arrays
 * after contract enforcement conversion. Prevents runtime errors.
 */

/**
 * Safely get string value from field that might be string or array
 * @param value - The field value (string, array, or undefined/null)
 * @param defaultValue - Default value if no valid value found
 * @returns First string value or default
 */
export function safeStringValue(value: any, defaultValue: string = ''): string {
  if (!value) return defaultValue;

  if (Array.isArray(value)) {
    // Return first non-empty element or default
    const firstValid = value.find(
      item => item && typeof item === 'string' && item.trim() !== ''
    );
    return firstValid || defaultValue;
  }

  if (typeof value === 'string') {
    return value.trim() || defaultValue;
  }

  // Convert other types to string
  return String(value) || defaultValue;
}

/**
 * Safely capitalize first letter of field that might be string or array
 * @param value - The field value (string, array, or undefined/null)
 * @param defaultValue - Default value if no valid value found
 * @returns Capitalized string or default
 */
export function safeCapitalize(
  value: any,
  defaultValue: string = 'Unknown'
): string {
  const stringValue = safeStringValue(value, defaultValue);
  if (stringValue === defaultValue) return defaultValue;

  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
}

/**
 * Safely convert field to lowercase that might be string or array
 * @param value - The field value (string, array, or undefined/null)
 * @param defaultValue - Default value if no valid value found
 * @returns Lowercase string or default
 */
export function safeLowerCase(value: any, defaultValue: string = ''): string {
  const stringValue = safeStringValue(value, defaultValue);
  return stringValue.toLowerCase();
}

/**
 * Safely replace text in field that might be string or array
 * @param value - The field value (string, array, or undefined/null)
 * @param searchValue - Text to search for
 * @param replaceValue - Text to replace with
 * @param defaultValue - Default value if no valid value found
 * @returns Modified string or default
 */
export function safeReplace(
  value: any,
  searchValue: string | RegExp,
  replaceValue: string,
  defaultValue: string = ''
): string {
  const stringValue = safeStringValue(value, defaultValue);
  if (stringValue === defaultValue) return defaultValue;

  return stringValue.replace(searchValue, replaceValue);
}

/**
 * Safely trim field that might be string or array
 * @param value - The field value (string, array, or undefined/null)
 * @param defaultValue - Default value if no valid value found
 * @returns Trimmed string or default
 */
export function safeTrim(value: any, defaultValue: string = ''): string {
  const stringValue = safeStringValue(value, defaultValue);
  return stringValue.trim();
}

/**
 * Safely display field in template literals - handles arrays by joining
 * @param value - The field value (string, array, or undefined/null)
 * @param joinSeparator - Separator for array elements
 * @param defaultValue - Default value if no valid value found
 * @returns Display string
 */
export function safeDisplayValue(
  value: any,
  joinSeparator: string = ', ',
  defaultValue: string = ''
): string {
  if (!value) return defaultValue;

  if (Array.isArray(value)) {
    const validItems = value.filter(
      item => item && typeof item === 'string' && item.trim() !== ''
    );
    return validItems.length > 0
      ? validItems.join(joinSeparator)
      : defaultValue;
  }

  if (typeof value === 'string') {
    return value.trim() || defaultValue;
  }

  return String(value) || defaultValue;
}

/**
 * Check if field contains specific value (works for both strings and arrays)
 * @param value - The field value (string, array, or undefined/null)
 * @param searchValue - Value to search for
 * @param caseSensitive - Whether search should be case sensitive
 * @returns Whether value is found
 */
export function safeIncludes(
  value: any,
  searchValue: string,
  caseSensitive: boolean = false
): boolean {
  if (!value || !searchValue) return false;

  const normalizedSearch = caseSensitive
    ? searchValue
    : searchValue.toLowerCase();

  if (Array.isArray(value)) {
    return value.some(item => {
      if (typeof item !== 'string') return false;
      const normalizedItem = caseSensitive ? item : item.toLowerCase();
      return normalizedItem.includes(normalizedSearch);
    });
  }

  if (typeof value === 'string') {
    const normalizedValue = caseSensitive ? value : value.toLowerCase();
    return normalizedValue.includes(normalizedSearch);
  }

  return false;
}
